import Link from "next/link";

import {
  Badge,
  Icon,
  LineClamp,
  visuallyHiddenStyles,
  FilesSVG,
  type SvgComponentType,
} from "@newsela/angelou";
import dayjs from "dayjs";

import BookmarkButton from "@/components/BookmarkButton";
import { BaseCardProps } from "./BaseCard.interface";

const DISPLAY_DATE_FORMAT = "MMM DD, YYYY";

import {
  anchorTagCss,
  badgeContainerCss,
  bookmarkButtonContainerCss,
  bookmarkButtonCss,
  cardContainerCss,
  detailsContainerCss,
  displayDateCss,
  featureTagCss,
  imageArea,
  imageCellCss,
  imgCss,
  imagePlaceholderCss,
  leftTagCss,
  metadataContainerCss,
  overlayTagContainerCss,
  rightTagCss,
  specialContentCss,
  titleLineClampCss,
  filesSVGCss,
  detailsContentCss,
} from "./BaseCard.styles";

const BaseCard = (props: BaseCardProps) => {
  const {
    badges = [],
    contentId,
    contentTag,
    dataQaSelector = "",
    detailsText,
    displayDate = "",
    featureTag,
    images = [],
    metadataOverlayTag,
    title = "",
    specialContent,
    url = "/",
    assetCount = 0,
  } = props;

  const imageCount = images.length;

  return (
    <article css={cardContainerCss}>
      <div css={imageArea(imageCount)}>
        {imageCount > 0 ? (
          images.slice(0, 4).map((img, i) => (
            <div key={`${img.url}-${i}`} css={imageCellCss}>
              <img
                src={img.formatted_url || img.url}
                alt={img.alt_text ?? ""}
                loading="lazy"
                css={imgCss}
              />
            </div>
          ))
        ) : (
          <div css={imagePlaceholderCss} />
        )}

        {badges && badges.length > 0 ? (
          <ul css={badgeContainerCss}>
            {badges?.map((badge, index) => {
              return (
                <Badge
                  key={`badge_${index}`}
                  flavor={Badge.flavor.solid}
                  {...badge}
                  size={Badge.size.md}
                  wrapperTagName="li"
                />
              );
            })}
          </ul>
        ) : null}

        <div css={overlayTagContainerCss}>
          {contentTag ? <div css={leftTagCss}>{contentTag}</div> : null}
          {metadataOverlayTag ? (
            <div css={rightTagCss}>
              {metadataOverlayTag.icon ? (
                <Icon SvgComponent={metadataOverlayTag.icon} />
              ) : null}
              <span css={visuallyHiddenStyles}>
                {metadataOverlayTag?.ariaLabel || ""}
              </span>
              <span aria-hidden="true">{metadataOverlayTag.value}</span>
            </div>
          ) : null}
          {assetCount && assetCount > 0 ? (
            <>
              <div css={leftTagCss}>
                <Icon
                  SvgComponent={FilesSVG as SvgComponentType}
                  __cssFor={filesSVGCss}
                />
                {assetCount.toString()}
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div css={metadataContainerCss}>
        <span css={displayDateCss}>
          {displayDate ? dayjs(displayDate).format(DISPLAY_DATE_FORMAT) : ""}
        </span>
        {featureTag ? (
          <span css={featureTagCss(featureTag?.backgroundColor || "")}>
            {featureTag.text}
          </span>
        ) : null}
      </div>

      <div>
        <LineClamp
          css={titleLineClampCss}
          innerElement="h3"
          text={title || ""}
          lines={3}
        />
      </div>

      {detailsText ? (
        <div css={detailsContainerCss}>
          <span css={detailsContentCss}>{detailsText}</span>
        </div>
      ) : null}

      <Link
        href={url}
        css={anchorTagCss}
        data-qa-selector={dataQaSelector || "content-card-link"}
      >
        <span aria-hidden="true" />
      </Link>

      {contentId ? (
        <div css={bookmarkButtonContainerCss}>
          <BookmarkButton
            contentId={contentId}
            __cssFor={{ root: bookmarkButtonCss }}
          />
        </div>
      ) : null}

      <div css={specialContentCss}>{specialContent}</div>
    </article>
  );
};

export default BaseCard;
